import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientHomePlanBaseData, ClientHomePlanHydrated } from "api-types/homePlans/homePlanResponses";
import type { ClientCommunityPlan } from "api-types/locationPlans/communityPlansResponses";
import type { ClientLocationPlan, ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import type { Doc } from "building-types/doc";
import type { FeatureSet } from "common/constants/features";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { Modify } from "common/utils/types";
import { docGetActiveOptionTemplateOverrides } from "doc/docGetOptionTemplates";
import mergeOptionTemplatesIntoOptionSets from "doc/options/mergeOptionTemplatesIntoOptionSets";
import { getReportingUnitsWithDefaultNamesFromDoc } from "doc/utils/labelTools";
import { defaultBrochureSettings } from "lib/state/brochureSettings";

export type ShowroomTemplatePlanData = Modify<ClientHomePlanBaseData, { lot?: ClientLotHydrated }>;

/**
 * Function used to feed public showroom UI, starting from only
 * a builder and a master plan. Assumes we have:
 *
 * 1. A builder
 * 2. A master plan
 * 3. An optional location
 * 4. An optional location plan
 *
 * Intended for use when creating a home plan with missing data
 */
export function createTemplatePlanData(
  builder: ClientBuilder,
  masterPlan: ClientMasterPlanHydrated,
): ShowroomTemplatePlanData {
  const now = new Date();

  const location: ClientLocationHydrated = {
    id: "",
    templateCode: null,
    builderId: builder.id,
    salesStaffId: null,
    salesStaff: undefined,
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: null,
    displayOptionPrices: true,
    disableLots: false,
    showMapTab: false,
    allowHomePlans: true,
    disableShowroomViewer: false,
    useOfficeAddress: false,
    officeAddress: null,
    officeCity: null,
    officeState: null,
    officeZip: null,
    officeCountry: null,
    brochureSlipSheetUrl: null,
    salesMapUrl: null,
    salesMapUseShowroomIntro: null,
    generalNotes: null,
    govtCodeAndStandards: null,
    buildingCodeAnalysisDesignCriteria: null,
    brochureSettings: null,
    pricingVisibilitySettings: null,
    homePlanCount: 0,
    locationPlanCount: 0,
    childrenCount: 0,
    lotsCount: 0,
    depth: 0,
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
    locationLevel: undefined,
    logoUrl: null,
    enableLogoOnMaps: false,
  };

  const locationPlan: ClientCommunityPlan = {
    id: "",
    communityId: null,
    masterPlanId: masterPlan.id,
    userId: null,
    name: `Your ${masterPlan.name}`,
    thumbnailUrl: masterPlan.thumbnailUrl,
    basePrice: masterPlan.basePrice || 0,
    stats: masterPlan.stats,
    overrides: null,
    planImages: null,
    archivedAt: null,
    planDownloadEnabled: true,
    disableNewOptions: false,
    createdAt: now,
    updatedAt: now,
    detached: false,
    detachedFromId: null,
  };

  const homePlan: ClientHomePlanHydrated = {
    id: "",
    builderId: builder.id,
    authorId: null,
    salesStaffId: null,
    salesStaff: undefined,
    lotId: null,
    customerId: null,
    planSetId: `Your ${masterPlan.name ?? "Master Plan"}`,
    masterPlanId: masterPlan.id,
    masterPlanName: masterPlan.name ?? "Master Plan",
    planName: `Your ${masterPlan.name ?? "Master Plan"}`,
    communityPlanId: "",
    customerInfo: {},
    optionChoices: null,
    builderOptionChoices: {},
    notes: null,
    lot: undefined,
    thumbnailUrl: masterPlan.thumbnailUrl,
    stats: null,
    doc: masterPlan.doc,
    planStatus: "PREP",
    lockedAssets: null,
    lockedAt: null,
    archivedAt: null,
    category: null,
    createdAt: now,
    updatedAt: now,
    detachedAt: null,
    masterPlanOptionTemplate: null,
    communityPlanOptionTemplate: null,
    studioDocVersionId: null,
    studioDocId: SHOWROOM_FAKE_STUDIO_DOC_ID,
  };

  return {
    builder,
    community: location,
    masterPlan,
    communityPlan: locationPlan,
    homePlan,
    lot: undefined,
    lotDiscounts: [],
    homePlanDiscounts: [],
  };
}

// Fake IDs must be 16 alphanumeric characters to match COMBINED_ID_REGEX
// used by Express route parameter validation. Short IDs cause 404 errors.
// All fake IDs start with "FAKE" prefix to enable detection via isShowroomFakeId().
const FAKE_ID_PREFIX = "FAKE";

export const SHOWROOM_FAKE_USER_ID = `${FAKE_ID_PREFIX}USER00010000`;
export const SHOWROOM_FAKE_BUILDER_ID = `${FAKE_ID_PREFIX}BUILD0010001`;
export const SHOWROOM_FAKE_AUTHOR_ID = `${FAKE_ID_PREFIX}AUTHR0010002`;

export const SHOWROOM_FAKE_COMMUNITY_ID = `${FAKE_ID_PREFIX}COMM00010003`;
export const SHOWROOM_FAKE_LOT_ID = `${FAKE_ID_PREFIX}LOT000010004`;

export const SHOWROOM_FAKE_MASTER_PLAN_ID = `${FAKE_ID_PREFIX}MPLAN0010005`;
export const SHOWROOM_FAKE_COMMUNITY_PLAN_ID = `${FAKE_ID_PREFIX}CPLAN0010006`;
export const SHOWROOM_FAKE_HOME_PLAN_ID = `${FAKE_ID_PREFIX}HPLAN0010007`;
export const SHOWROOM_FAKE_PLAN_SET_ID = `${FAKE_ID_PREFIX}PSET00010008`;

export const SHOWROOM_FAKE_TEMPLATE_CODE = `${FAKE_ID_PREFIX}TMPL00010009`;
export const SHOWROOM_FAKE_STUDIO_DOC_ID = `${FAKE_ID_PREFIX}SDOC00100010`;

/**
 * Checks if an ID is a fake showroom ID used in studio preview mode.
 * Fake IDs start with "FAKE" prefix and should not be used for API calls.
 */
export function isShowroomFakeId(id: string | undefined | null): boolean {
  return !!id && id.startsWith(FAKE_ID_PREFIX);
}

export function createFakePlanData(
  studioDoc: ClientStudioDocHydrated,
  enabledFeatures: FeatureSet,
): ClientHomePlanBaseData {
  const date = new Date("2021-01-01T00:00:00.000Z");

  // The hydrated studio doc carries the real builder via its `studioDocBuilders` relation. We
  // copy its branding metadata (companyName, logoUrl, salesConfig) onto the fake builder so the
  // showroom header / theming reflects the actual builder instead of "[Builder]". The fake ID
  // is preserved so `isShowroomFakeId()` guards can still filter any accidental real API calls.
  // `enabledProducts.showroom` is forced true because /showroom/studio is always a preview path
  // even when the real builder has not enabled the product.
  const realBuilder = studioDoc.studioDocBuilders?.[0]?.builder;

  const fakeBuilder: ClientBuilder = {
    id: SHOWROOM_FAKE_BUILDER_ID,
    category: realBuilder?.category ?? "DEMO",
    companyName: realBuilder?.companyName ?? "[Builder]",
    website: realBuilder?.website ?? null,
    phone: realBuilder?.phone ?? null,
    address: realBuilder?.address ?? null,
    city: realBuilder?.city ?? null,
    state: realBuilder?.state ?? null,
    zip: realBuilder?.zip ?? null,
    country: realBuilder?.country ?? null,
    logoUrl: realBuilder?.logoUrl ?? null,
    brochureSettings: realBuilder?.brochureSettings ?? defaultBrochureSettings,
    creditReloadThreshold: realBuilder?.creditReloadThreshold ?? null,
    creditReloadAmount: realBuilder?.creditReloadAmount ?? null,
    featureFlags: enabledFeatures,
    enabledProducts: { api: false, studio: true, showroom: true },
    salesConfig: realBuilder?.salesConfig ?? {},
    createdAt: realBuilder?.createdAt ?? date,
    updatedAt: realBuilder?.updatedAt ?? date,
  };

  // The studio doc's name is the only real plan-level name available pre-publish; community /
  // lot / community plan / master plan / home plan all derive from it so the showroom surfaces
  // the studio doc title instead of placeholders.
  const studioDocName = studioDoc.name;

  const fakeCommunity: ClientLocationHydrated = {
    id: SHOWROOM_FAKE_COMMUNITY_ID,
    templateCode: null,
    builderId: SHOWROOM_FAKE_BUILDER_ID,
    salesStaff: undefined,
    salesStaffId: null,
    name: studioDocName,
    address: "[Community address]",
    city: "[Community city]",
    state: "[Community state]",
    zip: "[Community zip]",
    country: "[Community country]",
    phone: null,
    displayOptionPrices: true,
    disableShowroomViewer: false,
    disableLots: false,
    showMapTab: false,
    allowHomePlans: true,
    useOfficeAddress: false,
    officeAddress: null,
    officeCity: null,
    officeState: null,
    officeZip: null,
    officeCountry: null,
    archivedAt: null,
    createdAt: date,
    updatedAt: date,
    brochureSlipSheetUrl: null,
    salesMapUrl: null,
    generalNotes: null,
    govtCodeAndStandards: null,
    buildingCodeAnalysisDesignCriteria: null,
    brochureSettings: null,
    pricingVisibilitySettings: null,
    homePlanCount: 0,
    locationPlanCount: 0,
    childrenCount: 0,
    lotsCount: 0,
    depth: 0,
    locationLevel: undefined,
    logoUrl: undefined,
    enableLogoOnMaps: false,
  };

  const fakeLot: ClientLotHydrated = {
    id: SHOWROOM_FAKE_LOT_ID,
    communityId: SHOWROOM_FAKE_COMMUNITY_ID,
    externalId: "1",
    spec: "spec",
    lotNumber: "1",
    address: null,
    price: null,
    phase: null,
    block: null,
    lotStatus: "available",
    lotTypes: [],
    lotFeatures: [],
    series: null,
    width: null,
    depth: null,
    area: null,
    releasedAt: date,
    createdAt: date,
    updatedAt: date,
    configuredHomePlans: [],
    garageHandingDirection: null,
    lotCommunityPlans: [],
    discounts: null,
    locationName: "",
  };

  const fakeCommunityPlan: ClientLocationPlan = {
    id: SHOWROOM_FAKE_COMMUNITY_PLAN_ID,
    locationId: SHOWROOM_FAKE_COMMUNITY_ID,
    masterPlanId: SHOWROOM_FAKE_MASTER_PLAN_ID,
    userId: SHOWROOM_FAKE_USER_ID,
    name: studioDocName,
    basePrice: 300000,
    overrides: null,
    thumbnailUrl: null,
    planImages: null,
    stats: {},
    planDownloadEnabled: true,
    disableNewOptions: false,
    archivedAt: null,
    createdAt: date,
    updatedAt: date,
    detached: false,
    detachedFromId: null,
  };

  const studioDocDoc = studioDoc.doc as Doc;
  const optionSets = mergeOptionTemplatesIntoOptionSets(studioDoc.doc.optionSets, [
    docGetActiveOptionTemplateOverrides(studioDocDoc),
  ]);
  const doc = { ...studioDocDoc, optionSets };
  const defaultReportingUnits = getReportingUnitsWithDefaultNamesFromDoc(doc).map((unit) => ({
    reportingUnitId: unit.id,
    name: unit.name,
    externalId: undefined,
    sortIndex: unit.sortIndex,
    createdAt: date,
  }));

  // Surface the first elevation name from the doc so the showroom can show the real elevation
  // label rather than "[Home plan elevation]". Beds/baths/area come from the rendered building
  // (ShowroomBuildingSync) so the fake values here are placeholders for the pricing path only.
  const elevationEntries = Object.values(studioDocDoc.elevations ?? {});
  const firstElevationName = elevationEntries[0]?.name ?? "";

  const fakeHomePlan: ClientHomePlanHydrated = {
    id: SHOWROOM_FAKE_HOME_PLAN_ID,
    builderId: SHOWROOM_FAKE_BUILDER_ID,
    authorId: SHOWROOM_FAKE_AUTHOR_ID,
    salesStaffId: null,
    salesStaff: undefined,
    communityPlanId: SHOWROOM_FAKE_COMMUNITY_PLAN_ID,
    planName: studioDocName,
    planSetId: SHOWROOM_FAKE_PLAN_SET_ID,
    customerId: null,
    customerInfo: {
      name: "",
    },
    lotId: SHOWROOM_FAKE_LOT_ID,
    lot: undefined,
    notes: null,
    optionChoices: {},
    builderOptionChoices: {},
    masterPlanId: SHOWROOM_FAKE_MASTER_PLAN_ID,
    masterPlanName: studioDocName,
    stats: {
      elevationName: firstElevationName,
      elevationId: "",
      bedroomCount: 0,
      bathroomCount: 0,
      totalFloorArea: 154,
      conditionedFloorArea: 154,
      basePrice: 300000,
      lotPrice: 0,
      discount: 0,
      optionsCount: 0,
      optionsPrice: 0,
      totalPrice: 300000,
      elevation: firstElevationName,
    },
    thumbnailUrl: null,
    lockedAssets: null,
    archivedAt: null,
    lockedAt: null,
    category: null,
    planStatus: "PREP",
    createdAt: date,
    updatedAt: date,
    detachedAt: null,
    doc,
    masterPlanOptionTemplate: null,
    communityPlanOptionTemplate: null,
    studioDocVersionId: null,
    // Use the real studio doc id here so useOptionChoicesSync keys off the actual doc, not the
    // fake constant. The fake constant was a leftover that would have lost option persistence.
    studioDocId: studioDoc.id,
  };

  const fakeMasterPlan: ClientMasterPlanHydrated = {
    id: SHOWROOM_FAKE_MASTER_PLAN_ID,
    templateCode: SHOWROOM_FAKE_TEMPLATE_CODE,
    studioDocId: studioDoc.id,
    builderId: SHOWROOM_FAKE_BUILDER_ID,
    name: studioDocName,
    thumbnailUrl: null,
    description: null,
    basePrice: 300000,
    stats: {},
    planImages: null,
    planDownloadEnabled: true,
    disableShowroomViewer: false,
    doc,
    overrides: null,
    optionEffectOverrides: null,
    createdAt: date,
    updatedAt: date,
    detached: false,
    detachedFromId: null,
    reportingUnits: defaultReportingUnits,
    builderName: realBuilder?.companyName ?? "[Builder]",
    authors: [],
  };

  return {
    builder: fakeBuilder,
    community: fakeCommunity,
    lot: fakeLot,
    communityPlan: fakeCommunityPlan,
    homePlan: fakeHomePlan,
    masterPlan: fakeMasterPlan,
    lotDiscounts: [],
    homePlanDiscounts: [],
  };
}

export function createLocationPlanData(locationPlanBaseData: ClientLocationPlanBaseData, lot?: ClientLotHydrated) {
  const { builder, masterPlan, location, locationPlan, optionSets, optionEffects, resolvedBasePrice } =
    locationPlanBaseData;
  const now = new Date();

  const doc: Doc = {
    ...masterPlan.doc,
    optionSets,
    optionEffects,
  };

  assertIsNotUndefined(location);
  const locationHydrated: ClientLocationHydrated = {
    ...location,
    salesStaff: undefined,
    locationPlanCount: 0,
    homePlanCount: 0,
    lotsCount: 0,
    locationLevel: undefined,
  };

  const homePlanHydrated: ClientHomePlanHydrated = {
    id: "",
    builderId: builder.id,
    authorId: null,
    salesStaffId: null,
    category: null,
    salesStaff: undefined,
    lotId: null,
    masterPlanId: masterPlan.id,
    planSetId: `Your ${masterPlan.name ?? "Master Plan"}`,
    masterPlanName: masterPlan.name ?? "Master Plan",
    planName: `Your ${masterPlan.name ?? "Master Plan"}`,
    communityPlanId: locationPlan.id,
    customerInfo: {},
    optionChoices: null,
    builderOptionChoices: {},
    notes: null,
    lot,
    thumbnailUrl: locationPlan.thumbnailUrl,
    stats: null,
    resolvedBasePrice,
    doc,
    planStatus: "PREP",
    lockedAssets: null,
    lockedAt: null,
    archivedAt: null,
    createdAt: now,
    updatedAt: now,
    detachedAt: null,
    masterPlanOptionTemplate: null,
    communityPlanOptionTemplate: null,
    lotCommunityPlanOptionTemplate: null,
    studioDocVersionId: null,
    studioDocId: masterPlan.studioDocId,
  };

  return {
    masterPlan,
    location,
    locationPlan,
    locationHydrated,
    homePlanHydrated,
    elevationOverrides: locationPlanBaseData.elevationOverrides,
  };
}

export function createLotCommunityPlanData(
  locationPlanBaseData: ClientLocationPlanBaseData,
  lot: ClientLotHydrated | undefined,
  lotCommunityPlan: ClientLotCommunityPlanHydrated,
) {
  const { masterPlan, location, locationPlan, locationHydrated, homePlanHydrated, elevationOverrides } =
    createLocationPlanData(locationPlanBaseData, lot);

  // For multi-unit buildings, do NOT apply lot option templates here.
  // The showroom will dynamically apply lot overrides based on the selected unit,
  // allowing correct switching between units with different lots.
  const isMultiUnit = (locationPlanBaseData.reportingUnitLots?.length ?? 0) > 1;

  const doc: Doc = isMultiUnit
    ? homePlanHydrated.doc
    : {
        ...homePlanHydrated.doc,
        optionSets: mergeOptionTemplatesIntoOptionSets(homePlanHydrated.doc.optionSets, [
          lotCommunityPlan.optionSetOverrides,
        ]),
      };

  return {
    masterPlan,
    location,
    locationPlan,
    locationHydrated,
    homePlanHydrated: { ...homePlanHydrated, doc },
    elevationOverrides,
  };
}
